<script setup lang="ts">
import { ref } from 'vue'
import type { VpnKey } from '@/types'
import QrModal from '@/components/keys/QrModal.vue'
import KeyConfigModal from '@/components/keys/KeyConfigModal.vue'
import LocationDisplay from '@/components/ui/LocationDisplay.vue'
import ServerHealthIndicators from '@/components/health/ServerHealthIndicators.vue'
import SublinkIncludeSwitch from '@/components/keys/SublinkIncludeSwitch.vue'
import { emptyHealthBars, useServerHealthStore } from '@/stores/serverHealth'
import type { HealthBar } from '@/stores/serverHealth'
import { useUserStore } from '@/stores/user'
import { isKeyExcludedBySubscriptionFilters, keyNumberLabel, protocolLabel } from '@/utils/keys'

defineProps<{ keys: VpnKey[] }>()
const health = useServerHealthStore()
const user = useUserStore()

const qrOpen = ref(false)
const qrData = ref('')
const qrTitle = ref('')
const keyOpen = ref(false)
const keyConfig = ref('')
const keyTitle = ref('')

function openQr(key: VpnKey) {
  qrData.value = key.qr_data || key.config_string || ''
  qrTitle.value = key.server_name
  qrOpen.value = true
}

function openKey(key: VpnKey) {
  keyConfig.value = key.config_string || ''
  keyTitle.value = key.server_name
  keyOpen.value = true
}

function healthBarTitle(bar: HealthBar): string {
  if (!bar.timeRange) return 'Нет данных'
  const suffix = bar.current ? ' — текущий час' : ''
  if (bar.total === 0) return `${bar.timeRange} — нет данных${suffix}`
  const errs = bar.failed > 0 ? `${bar.failed} ошибок из ${bar.total}` : `без ошибок (${bar.total} проверок)`
  const partial = bar.partial ? ', данных за час пока мало' : ''
  return `${bar.timeRange} — ${errs}${partial}${suffix}`
}
</script>

<template>
  <div class="panel !p-0 overflow-hidden">
    <!-- Mobile: карточки без горизонтального скролла -->
    <div class="divide-y divide-white-light/60 md:hidden">
      <article
        v-for="key in keys"
        :key="key.id"
        class="space-y-2 p-3"
      >
        <div class="font-semibold leading-snug">{{ key.server_name }}</div>
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted">
          <LocationDisplay
            :country-code="key.country_code"
            :location="key.location"
            inline
            :size="16"
          />
          <span>· {{ protocolLabel(key) }}</span>
        </div>
        <div class="flex flex-wrap items-center gap-2 text-xs">
          <template v-if="key.is_expired">
            <span class="badge badge-danger">Истек</span>
          </template>
          <ServerHealthIndicators
            v-else-if="key.display_server_id"
            :server-id="key.display_server_id"
            :transit-server-id="key.transit_server_id"
            compact
          />
        </div>
        <div class="flex flex-wrap items-center justify-between gap-2 pt-1">
          <div class="inline-flex min-w-0 items-center gap-1.5">
            <span
              v-if="keyNumberLabel(key)"
              class="text-xs font-bold leading-none"
            >
              {{ keyNumberLabel(key) }}
            </span>
            <SublinkIncludeSwitch
              :key-id="key.id"
              :model-value="key.include_in_sublink !== false"
              :blocked-by-filter="isKeyExcludedBySubscriptionFilters(key, user.subscriptionFilters)"
            />
          </div>
          <div class="flex shrink-0 gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="!key.config_string"
              @click="openKey(key)"
            >
              Ключ
            </button>
            <button
              type="button"
              class="btn btn-sm btn-outline-primary"
              :disabled="!(key.qr_data || key.config_string)"
              @click="openQr(key)"
            >
              QR
            </button>
          </div>
        </div>
      </article>
    </div>

    <!-- Desktop: таблица -->
    <div class="hidden overflow-x-auto md:block">
      <table class="w-full min-w-[960px] text-sm">
        <thead class="border-b border-white-light bg-panel-header text-left">
          <tr>
            <th class="px-4 py-3 font-bold">Сервер</th>
            <th class="hidden px-4 py-3 font-bold md:table-cell">Локация</th>
            <th class="hidden px-4 py-3 font-bold lg:table-cell">Протокол</th>
            <th class="px-4 py-3 font-bold">Статус</th>
            <th class="hidden px-4 py-3 font-bold md:table-cell">Загрузка</th>
            <th class="hidden px-4 py-3 font-bold md:table-cell">Пинг</th>
            <th class="px-4 py-3 font-bold">Действия</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="key in keys"
            :key="key.id"
            class="border-b border-white-light/60 last:border-0 hover:bg-black/[0.02]"
          >
            <td class="px-4 py-3">
              <strong>{{ key.server_name }}</strong>
              <div class="text-xs text-muted md:hidden">
                <LocationDisplay
                  :country-code="key.country_code"
                  :location="key.location"
                  inline
                  :size="16"
                />
                <span> · {{ protocolLabel(key) }}</span>
              </div>
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              <LocationDisplay
                :country-code="key.country_code"
                :location="key.location"
                :size="20"
              />
            </td>
            <td class="hidden px-4 py-3 lg:table-cell">{{ protocolLabel(key) }}</td>
            <td class="px-4 py-3">
              <template v-if="key.is_expired">
                <span class="badge badge-danger">Истек</span>
              </template>
              <template v-else-if="key.display_server_id">
                <div class="health-scale inline-flex">
                  <div
                    v-for="(bar, idx) in (health.history[key.display_server_id] || emptyHealthBars())"
                    :key="idx"
                    class="health-scale-bar"
                    :class="[bar.color, { current: bar.current }]"
                    :title="healthBarTitle(bar)"
                  />
                </div>
                <span
                  v-if="key.transit_server_id"
                  class="transit-indicator align-middle"
                  :style="{
                    backgroundColor: health.transitOnline(key.transit_server_id) ? '#28a745' : '#dc3545',
                  }"
                  title="Статус транзитного сервера"
                />
              </template>
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              <span v-if="key.is_expired" class="badge badge-danger">Истек</span>
              <span
                v-else-if="key.display_server_id"
                class="badge"
                :class="health.onlineBadgeClass(key.display_server_id)"
                :title="health.onlineTitle(key.display_server_id)"
              >
                {{ health.onlineLabel(key.display_server_id) }}
              </span>
            </td>
            <td class="hidden px-4 py-3 md:table-cell">
              <span
                v-if="!key.is_expired && key.display_server_id"
                class="badge"
                :class="health.pingBadgeClass(key.display_server_id)"
              >
                {{ health.pingLabel(key.display_server_id) }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td class="px-4 py-3 align-middle">
              <div class="flex flex-wrap items-center justify-end gap-2">
                <div class="inline-flex items-center gap-1.5 shrink-0">
                  <span
                    v-if="key.key_number"
                    class="inline-flex items-center text-xs font-bold leading-none text-black whitespace-nowrap dark:text-white"
                  >
                    Ключ {{ key.key_number }}
                  </span>
                  <SublinkIncludeSwitch
                    :key-id="key.id"
                    :model-value="key.include_in_sublink !== false"
                    :blocked-by-filter="isKeyExcludedBySubscriptionFilters(key, user.subscriptionFilters)"
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!key.config_string"
                  @click="openKey(key)"
                >
                  Ключ
                </button>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="!(key.qr_data || key.config_string)"
                  @click="openQr(key)"
                >
                  QR
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <QrModal :open="qrOpen" :data="qrData" :title="qrTitle" @close="qrOpen = false" />
    <KeyConfigModal :open="keyOpen" :config="keyConfig" :title="keyTitle" @close="keyOpen = false" />
  </div>
</template>
